import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Plus, Trash2, Eye, Star } from 'lucide-react';
import { toast } from 'sonner';

const AuditSheets = () => {
  const { user } = useAuth();
  const [auditSheets, setAuditSheets] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customName, setCustomName] = useState('');

  const [customSheet, setCustomSheet] = useState({
    name: '',
    description: '',
    type: 'custom',
    questions: [
      { question: '', type: 'rating', weight: 10, category: '', required: true }
    ]
  });

  useEffect(() => {
    fetchAuditSheets();
    fetchTemplates();
  }, []);

  const fetchAuditSheets = async () => {
    try {
      const response = await axios.get(`${API}/audit-sheets`);
      setAuditSheets(response.data);
    } catch (error) {
      console.error('Failed to fetch audit sheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/audit-sheets/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    try {
      await axios.post(`${API}/audit-sheets/from-template/${selectedTemplate}`, null, {
        params: { custom_name: customName || undefined }
      });
      toast.success('Audit sheet created from template!');
      setIsTemplateDialogOpen(false);
      setSelectedTemplate('');
      setCustomName('');
      fetchAuditSheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create audit sheet');
    }
  };

  const handleCreateCustom = async (e) => {
    e.preventDefault();
    
    // Validate questions
    const validQuestions = customSheet.questions.filter(q => q.question.trim() !== '');
    if (validQuestions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    try {
      await axios.post(`${API}/audit-sheets`, {
        ...customSheet,
        questions: validQuestions
      });
      toast.success('Custom audit sheet created!');
      setIsCreateDialogOpen(false);
      setCustomSheet({
        name: '',
        description: '',
        type: 'custom',
        questions: [{ question: '', type: 'rating', weight: 10, category: '', required: true }]
      });
      fetchAuditSheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create audit sheet');
    }
  };

  const handleDelete = async (sheetId) => {
    if (!window.confirm('Are you sure you want to delete this audit sheet?')) return;

    try {
      await axios.delete(`${API}/audit-sheets/${sheetId}`);
      toast.success('Audit sheet deleted!');
      fetchAuditSheets();
    } catch (error) {
      toast.error('Failed to delete audit sheet');
    }
  };

  const addQuestion = () => {
    setCustomSheet({
      ...customSheet,
      questions: [...customSheet.questions, { question: '', type: 'rating', weight: 10, category: '', required: true }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...customSheet.questions];
    newQuestions[index][field] = value;
    setCustomSheet({ ...customSheet, questions: newQuestions });
  };

  const removeQuestion = (index) => {
    const newQuestions = customSheet.questions.filter((_, i) => i !== index);
    setCustomSheet({ ...customSheet, questions: newQuestions });
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      boolean: 'Yes/No',
      rating: 'Rating (1-5)',
      text: 'Text Response',
      multiple_choice: 'Multiple Choice'
    };
    return labels[type] || type;
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="audit-sheets-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Audit Sheets
            </h1>
            <p className="text-slate-600">Structured assessment forms for call quality evaluation</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300" data-testid="use-template-button">
                  <Star size={20} className="mr-2" />
                  Use Template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create from Template</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="template-select">Select Template</Label>
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger id="template-select" data-testid="template-select">
                        <SelectValue placeholder="Choose a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(templates).map((key) => (
                          <SelectItem key={key} value={key}>
                            {templates[key].name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTemplate && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-slate-900 mb-1">
                        {templates[selectedTemplate]?.name}
                      </p>
                      <p className="text-xs text-slate-600 mb-3">
                        {templates[selectedTemplate]?.description}
                      </p>
                      <p className="text-xs text-slate-700">
                        {templates[selectedTemplate]?.questions?.length || 0} questions
                      </p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="custom-name">Custom Name (Optional)</Label>
                    <Input
                      id="custom-name"
                      placeholder="e.g., Production Customer Service Audit"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      data-testid="custom-name-input"
                    />
                  </div>

                  <Button
                    onClick={handleCreateFromTemplate}
                    className="w-full"
                    disabled={!selectedTemplate}
                    data-testid="create-from-template-button"
                  >
                    Create Audit Sheet
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="create-custom-button">
                  <Plus size={20} className="mr-2" />
                  Create Custom
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Custom Audit Sheet</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCustom} className="space-y-4">
                  <div>
                    <Label htmlFor="sheet-name">Sheet Name</Label>
                    <Input
                      id="sheet-name"
                      placeholder="e.g., Custom Call Audit"
                      value={customSheet.name}
                      onChange={(e) => setCustomSheet({ ...customSheet, name: e.target.value })}
                      required
                      data-testid="sheet-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sheet-description">Description</Label>
                    <Textarea
                      id="sheet-description"
                      placeholder="Brief description"
                      value={customSheet.description}
                      onChange={(e) => setCustomSheet({ ...customSheet, description: e.target.value })}
                      data-testid="sheet-description-input"
                    />
                  </div>

                  <div>
                    <Label>Questions</Label>
                    <div className="space-y-3 mt-2">
                      {customSheet.questions.map((q, index) => (
                        <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Question text"
                              value={q.question}
                              onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                              data-testid={`question-${index}`}
                              className="flex-1"
                            />
                            {customSheet.questions.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeQuestion(index)}
                                data-testid={`remove-question-${index}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs">Type</Label>
                              <Select
                                value={q.type}
                                onValueChange={(value) => updateQuestion(index, 'type', value)}
                              >
                                <SelectTrigger className="h-9" data-testid={`type-${index}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="boolean">Yes/No</SelectItem>
                                  <SelectItem value="rating">Rating (1-5)</SelectItem>
                                  <SelectItem value="text">Text</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Weight</Label>
                              <Input
                                type="number"
                                value={q.weight}
                                onChange={(e) => updateQuestion(index, 'weight', parseInt(e.target.value))}
                                min="1"
                                className="h-9"
                                data-testid={`weight-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Category</Label>
                              <Input
                                placeholder="e.g., Opening"
                                value={q.category}
                                onChange={(e) => updateQuestion(index, 'category', e.target.value)}
                                className="h-9"
                                data-testid={`category-${index}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addQuestion}
                      className="mt-2"
                      data-testid="add-question-button"
                    >
                      <Plus size={16} className="mr-1" />
                      Add Question
                    </Button>
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-custom-sheet-button">
                    Create Audit Sheet
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Audit Sheets List */}
        {loading ? (
          <p className="text-slate-600">Loading audit sheets...</p>
        ) : auditSheets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auditSheets.map((sheet) => (
              <Card
                key={sheet.id}
                className="p-6 border border-slate-200 shadow-sm card-hover"
                data-testid={`audit-sheet-${sheet.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <FileText size={24} className="text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-slate-900">{sheet.name}</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(sheet.id)}
                        data-testid={`delete-${sheet.id}`}
                      >
                        <Trash2 size={16} className="text-slate-600" />
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">{sheet.description || 'No description'}</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          {sheet.type === 'standard' ? 'Template' : 'Custom'}
                        </span>
                        <span className="text-xs text-slate-600">
                          {sheet.questions?.length || 0} questions
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Max Score: {sheet.total_score}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border border-slate-200 shadow-sm">
            <FileText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">No audit sheets created yet</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setIsTemplateDialogOpen(true)} data-testid="create-from-template-empty">
                Use Template
              </Button>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(true)} data-testid="create-custom-empty">
                Create Custom
              </Button>
            </div>
          </Card>
        )}

        {/* Standard Templates Info */}
        <Card className="p-6 border border-blue-200 bg-blue-50 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Available Standard Templates
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(templates).map((key) => (
              <div key={key} className="p-3 bg-white border border-slate-200 rounded-lg">
                <p className="font-medium text-slate-900 mb-1">{templates[key].name}</p>
                <p className="text-xs text-slate-600">{templates[key].description}</p>
                <p className="text-xs text-slate-500 mt-2">
                  {templates[key].questions?.length || 0} questions
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AuditSheets;